import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import Icon from '../docs/Icon'
import AgencyMark from '../marks/AgencyMark'
import { BANNER } from '@/content/identity'
import { useState } from 'react'
import { agencies, america, findMission, type Mission } from '@/content/missions'
import { usePrefs } from '@/state/prefs'
import { useReducedMotion } from '@/state/useReducedMotion'
import Cased from '../ui/Cased'
import { useSurprise } from '@/state/useSurprise'

/** A miniature site in the mission's own theme: the preview is the product. */
export function MissionPreview({ mission, mode }: { mission: Mission; mode?: 'light' | 'dark' }) {
  const { prefs } = usePrefs()
  const nation = mission.kind === 'nation'
  return (
    <div className="mission-preview" data-mission={mission.id} {...(mode ? { 'data-theme': mode } : {})}>
      <div className="mission-preview__banner">{nation ? BANNER.site : BANNER.official}</div>
      <div className="mission-preview__bar">
        <AgencyMark mission={mission} variant={prefs.mark} className="mission-preview__mark" />
        <span className="mission-preview__name">{nation ? 'America' : mission.short}</span>
        <span className="mission-preview__menu">Menu</span>
      </div>
      <div className="mission-preview__hero">
        <p className="mission-preview__title">{nation ? 'Hello, America' : mission.name}</p>
        <p className="mission-preview__quote">“{mission.mission.text}”</p>
        <span className="mission-preview__pill">
          <span>How can we help?</span>
          <span className="mission-preview__send">
            <Icon name="arrow" size={12} />
          </span>
        </span>
      </div>
    </div>
  )
}

function useParallax() {
  const ref = useRef<HTMLDivElement>(null)
  const reduced = useReducedMotion()
  useEffect(() => {
    const el = ref.current
    if (!el || reduced) return
    let frame = 0
    const render = () => {
      frame = 0
      const rect = el.getBoundingClientRect()
      const offset = (rect.top + rect.height / 2 - window.innerHeight / 2) / window.innerHeight
      el.style.setProperty('--parallax', offset.toFixed(4))
    }
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(render)
    }
    render()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      cancelAnimationFrame(frame)
    }
  }, [reduced])
  return ref
}

/** America, the applied mission if any, and a random sample to fill four cards. */
function useFeatured(applied: string): Mission[] {
  const [sample] = useState(() => [...agencies].sort(() => Math.random() - 0.5).slice(0, 3))
  const chosen = applied !== america.id ? findMission(applied) : undefined
  const rest = sample.filter((mission) => mission.id !== chosen?.id)
  return [america, ...(chosen ? [chosen] : []), ...rest].slice(0, 4)
}

export default function MissionsShowcase() {
  const { prefs, set } = usePrefs()
  const { surprise, announcement } = useSurprise()
  const featured = useFeatured(prefs.mission)
  const ref = useParallax()
  return (
    <section className="mds-section home-missions" aria-labelledby="missions-title">
      <div className="mds-container">
        <div className="mds-section-head mds-section-head--center">
          <span className="mds-eyebrow">Select a mission</span>
          <h2 id="missions-title" className="mds-display">
            <Cased>Every agency, ready on day one.</Cased>
          </h2>
          <p className="mds-lead">
            Each mission theme is pregenerated from the agency's own colors and seal, checked for contrast in light and
            dark, and published as CSS and JSON. Pick one and this site becomes it.
          </p>
        </div>
        <div ref={ref} className="home-missions__grid">
          {featured.map((mission, index) => (
            <article key={mission.id} className="home-missions__item" style={{ '--depth': index % 2 ? 1 : -1 } as React.CSSProperties}>
              <MissionPreview mission={mission} />
              <div className="home-missions__meta">
                <div>
                  <h3 className="mds-card__title" title={mission.name}>
                    {mission.name}
                  </h3>
                  <p className="mds-muted">
                    {mission.kind === 'nation' ? <Cased>Default theme</Cased> : `Est. ${mission.established}`}
                  </p>
                </div>
                <div className="mds-cluster">
                  <button
                    type="button"
                    className="mds-button mds-button--primary mds-button--sm"
                    aria-pressed={prefs.mission === mission.id}
                    onClick={() => set('mission', mission.id)}
                  >
                    {prefs.mission === mission.id ? 'Applied' : 'Apply'}
                    <span className="mds-visually-hidden"> {mission.name} theme</span>
                  </button>
                  <Link to={`/missions/${mission.id}`} className="mds-button mds-button--ghost mds-button--sm">
                    Details<span className="mds-visually-hidden"> for {mission.name}</span>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>
        <p className="home-missions__more">
          <Link to="/missions" className="mds-link">
            <Cased>Browse every mission theme</Cased>
          </Link>
          <button type="button" className="mds-button mds-button--ghost" onClick={surprise}>
            <Icon name="spark" size={16} /> <Cased>Surprise me</Cased>
          </button>
        </p>
        <p className="mds-visually-hidden" role="status">
          {announcement}
        </p>
      </div>
    </section>
  )
}
